// @ts-ignore
import Config from 'Config';
import { DayOfWeek, PurchaserRole, TimeslotFrequencyInput } from 'globalTypes';
import { Option } from 'components/Select';

export type CoachingSessionFrequency =
  | 'once'
  | 'weeklyOngoing'
  | 'weeklyWithEnd'
  | 'everyOtherWeekOngoing'
  | 'everyOtherWeekWithEnd';
export type CoachingSessionDuration = 40 | 60;
export type CoachAvailabilityInterval = {
  startTime: string;
  endTime: string;
};

const WEEKDAY_TECH_SUPPORT_HOURS = '11am–8pm PT';
const WEEKEND_TECH_SUPPORT_HOURS = '6:30am–5:30pm PT';

const WEEKDAY_SCHEDULING_HOURS = '12pm to 8pm Pacific time';
const WEEKEND_SCHEDULING_HOURS = '7am to 6pm Pacific time';

const COACHING_HOME_ROUTE = '/coaching/home';
const TUTORING_HELP_FORM_ROUTE = '/tutoring/help/form';

const ZENDESK = 'https://help.remind.com/hc/en-us';

export default {
  COACHING_HELP_EMAIL: 'tutoring-help@remind.com',
  COACHING_HELP_EMAIL_LINK: `'mailto:tutoring-help@remind.com'`,
  DEFAULT_SIGNED_IN_ROUTE: '/log_in',
  GENERALIZED_SIGN_IN_ROUTE: '/sign_in',
  TUTORING_GET_STARTED: '/tutoring/start',
  TUTORING_SCHEDULE: '/tutoring/schedule',
  TUTORING_STUDENT_DETAILS: '/tutoring/details',
  TUTORING_BILLING: '/tutoring/billing',
  TUTORING_LINK_STUDENT: '/tutoring/link_student',
  TUTORING_SUMMARY: '/tutoring/summary', 
  TUTORING_HELP_FORM_ROUTE,
  TUTORING_COACH_HELP_FORM_ROUTE: '/tutoring/coach_help/form',

  ZENDESK,
  TUTORING_HELP_ROUTE: `${ZENDESK}/categories/4413838243213`,
  TUTORING_COACH_HELP_ROUTE: `${ZENDESK}/categories/4413833073677`,
  
  COACHING_GET_APP_ROUTE: '/get_the_app',
  COACHING_PURCHASE_REVIEW_ROUTE: '/coaching/review',
  COACHING_PURCHASE_REVIEW_SETUP_FINISHED_ROUTE: '/coaching/review?setupFinished=true',
  COACHING_SCHEDULE_ROUTE: '/coaching/schedule',
  COACHING_SCHEDULE_MOBILE_ROUTE: '/coaching/schedule/mobile',
  COACHING_AVAILABILITY_ROUTE: '/coaching/availability',
  COACHING_AVAILABILITY_MOBILE_ROUTE: '/coaching/availability/mobile',
  COACHING_BILLING_ROUTE: '/coaching/billing',
  COACHING_BILLING_MOBILE_ROUTE: '/coaching/billing/mobile',
  COACHING_HOME_ROUTE,
  COACHING_HOME_MOBILE_ROUTE: '/coaching/home/mobile',
  COACHING_COORDINATOR_ROUTE: '/coaching/coordinator',
  COACHING_SESSION_ROUTE: '/coaching/session',
  COACHING_CHAT_STREAM_ROUTE_PREFIX: '/coaching/messages',
  COACHING_CHAT_STREAM_ROUTE: '/coaching/messages/:streamUuid',
  COACHING_ACCEPT_INVITATION_ROUTE: '/coaching/accept',
  COACHING_FINALIZE_PURCHASE_ROUTE: '/coaching/finalize_purchase',
  IS_INTENDED_COACHING_USER_COOKIE: 'isIntendedCoachingUser',
  MANAGE_COACH_AVAILABILITY: 'Manage your Remind Tutoring availability',
  COACHING_SCHEDULING_HOURS: `Remind Tutoring is available Monday through Friday from ${WEEKDAY_SCHEDULING_HOURS} and Saturday through Sunday from ${WEEKEND_SCHEDULING_HOURS}.`,
  /* Support and chat hours */
  COACHING_HELP_HOURS: `We typically respond within 1–2 business hours, Monday through Friday from ${WEEKDAY_TECH_SUPPORT_HOURS} and Saturday through Sunday from ${WEEKEND_TECH_SUPPORT_HOURS}.`,
  COACHING_COACH_HELP_HOURS: [
    'Response time: 1–2 hours',
    `Mon–Fri, ${WEEKDAY_TECH_SUPPORT_HOURS}`,
    `Sat–Sun, ${WEEKEND_TECH_SUPPORT_HOURS}`,
  ],
  COACHING_CHAT_HOURS: `Chat is available Monday–Friday from ${WEEKDAY_TECH_SUPPORT_HOURS} and Saturday–Sunday from ${WEEKEND_TECH_SUPPORT_HOURS}`,
  COACHING_COACH_CHAT_HOURS: `Chat with a Remind team member Monday through Friday, ${WEEKDAY_TECH_SUPPORT_HOURS} and Saturday through Sunday, ${WEEKEND_TECH_SUPPORT_HOURS}.`,
  /************************/
  TESTIMONIALS: [
    {
      quoteText:
        'For the first session, I sat in the background and was very impressed. After the first session, I was able to get chores done around the house.',
      authorName: 'Nicole',
      authorDescription: '12th Grade Parent',
    },
    {
      quoteText:
        'I’d never before considered online tutoring for my kids, but this session changed my mind.',
      authorName: 'Laura',
      authorDescription: '8th Grade Parent',
    },
  ],
  VALID_COACHING_DAYS: [
    DayOfWeek.Monday,
    DayOfWeek.Tuesday,
    DayOfWeek.Wednesday,
    DayOfWeek.Thursday,
    DayOfWeek.Friday,
    DayOfWeek.Saturday,
    DayOfWeek.Sunday,
  ],
  COACHING_SESSION_DURATIONS: [40, 60] as CoachingSessionDuration[],
  START_OF_COACHING_HOURS_IN_PACIFIC: 7, // 7AM absolute hour e.g. 0-23
  END_OF_COACHING_HOURS_IN_PACIFIC: 20, // 8PM absolute hour e.g. 0-23
  SORTED_DAYS_OF_WEEK: [
    DayOfWeek.Sunday,
    DayOfWeek.Monday,
    DayOfWeek.Tuesday,
    DayOfWeek.Wednesday,
    DayOfWeek.Thursday,
    DayOfWeek.Friday,
    DayOfWeek.Saturday,
  ],
  TIMEZONES_TO_EXCLUDE: ['AKST', 'AKDT', 'HST', 'HDT'],
  COACHING_FREQUENCIES_FOR_COORDINATOR: [
    {
      label: 'Once',
      value: 'once',
    },
    {
      label: 'Weekly - Ongoing',
      value: 'weeklyOngoing',
    },
    {
      label: 'Weekly - Ends on date',
      value: 'weeklyWithEnd',
    },
    {
      label: 'Every Other Week - Ongoing',
      value: 'everyOtherWeekOngoing',
    },
    {
      label: 'Every Other Week - Ends on date',
      value: 'everyOtherWeekWithEnd',
    },
  ] as Option[],
  COACHING_FREQUENCIES: [
    {
      label: 'Weekly',
      value: TimeslotFrequencyInput.weekly,
    },
    {
      label: 'Every other week',
      value: TimeslotFrequencyInput.everyOtherWeek,
    },
    {
      label: 'Just once',
      value: TimeslotFrequencyInput.once,
    },
  ] as Option[],
  HELP_CONTACT_REASONS: [
    { label: 'Book more sessions', value: 'c_new_session' },
    { label: 'Reschedule a session', value: 'c_reschedule_session' },
    { label: 'Cancel a session', value: 'c_cancellation' },
    { label: 'Update schedule', value: 'c_update_plan' },
    { label: 'Report a billing issue', value: 'c_billing_issue' },
    { label: 'Request tech support', value: 'c_tech_support' },
    { label: 'Report a session issue', value: 'c_session_issue' },
    { label: 'General question', value: 'c_general_question' },
    { label: 'New customer inquiry', value: 'c_new_customer_inquiry' },
  ] as Option[],
  COACH_HELP_CONTACT_REASONS: [
    { label: 'Report a scheduling issue', value: 'c_coach_scheduling_issue' },
    { label: 'Report a payment issue', value: 'c_coach_payment_issue' },
    { label: 'Request tech support', value: 'c_coach_tech_support' },
    { label: 'Other', value: 'c_coach_other' },
  ] as Option[],
  CONTACT_REASON_LINKS: [
    {
      subject: 'Session',
      links: [
        {
          label: 'Request tech support',
          to: `${TUTORING_HELP_FORM_ROUTE}?request=c_tech_support`,
        },
        {
          label: 'Report a session issue',
          to: `${TUTORING_HELP_FORM_ROUTE}?request=c_session_issue`,
        },
      ],
    },
    {
      subject: 'Other',
      links: [
        {
          label: 'Report a billing issue',
          to: `${TUTORING_HELP_FORM_ROUTE}?request=c_billing_issue`,
        },
        {
          label: 'Send a general question',
          to: `${TUTORING_HELP_FORM_ROUTE}?request=c_general_question`,
        },
      ],
    },
    {
      subject: 'Scheduling',
      links: [],
      buttons: [
        {
          description: 'Make schedule changes in your dashboard',
          label: 'Go to dashboard',
          to: COACHING_HOME_ROUTE,
        },
      ],
    },
  ] as ReadonlyArray<ContactReasonLink>,
  COACHING_SESSIONS_PAGE_LENGTH: 4,
  COACHING_SESSION_RECORDINGS_LIMIT: 5,
  DELETE_COACHING_SESSION_MODAL_ONGOING: {
    headerText: 'Remove ongoing series',
    confirmText: 'Remove all upcoming',
    cancelText: 'Cancel',
  },
  DELETE_COACHING_SESSION_MODAL_SINGLE: {
    headerText: 'Remove session',
    confirmText: 'Remove session',
    cancelText: 'Cancel',
  },
  MIN_COACH_PAUSE_LENGTH_IN_WEEKS: 3,
  TUTOR_QUESTIONS_LINK: 'https://help.remind.com/hc/en-us/sections/360013541232-Remind-Tutoring',
  TUTOR_DOCS: 'https://www.dropbox.com/sh/s8hep3mx9m66acw/AAAWi2X2ntmc0P9wLcfBR5rza?dl=0',
  TUTORING_WAITLIST_SURVEY_URL: 'https://www.surveymonkey.com/r/rmdtutoringwaitlist',
  R101_ZENDESK_COACHING_URL: Config.Environment.R101_ZENDESK_COACHING_URL,
  ZENDESK_API_TOKEN: Config.Environment.ZENDESK_API_TOKEN,
  TUTORING_STREAMLINED_TUTORING_ROLES: [
    {
      role: PurchaserRole.Parent,
      label: 'My student',
    },
    {
      role: PurchaserRole.Student,
      label: 'Myself',
    },
  ],
  COORDINATOR_TOOL_EVENT_PREFIX: 'tutoring_coordinator_tool',
} as const;

type ContactReasonLink = {
  subject: string;
  links: ReadonlyArray<{ label: string; to: string }>;
  buttons?: ReadonlyArray<{ description: string; label: string; to: string }>;
};
